import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  LayoutDashboard,
  BarChart3,
  Users,
  UsersRound,
  FileSpreadsheet,
  Settings,
  Search,
  LogOut,
  ShieldCheck,
  Building2,
  Truck,
  ScrollText,
  BadgeCheck,
  Target,
  HardHat,
  Cable,
  Wrench,        // Operations
  TrendingUp,    // Sales
  FileSignature, // Contracts
  FileBarChart,  // Reports
  Sparkles,      // Report Builder (AI)
  Warehouse,     // Asset Intelligence
  Upload,        // Import Hub
  X,             // close (mobile)
  LifeBuoy,      // Support
  MessageCircle, // Feedback & CSAT
} from "lucide-react";
import { GBELogo } from "./GBELogo";
import { TID } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { useSidebar } from "@/context/SidebarContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// ============================================================
// GBE Hub — 12 modules per Enterprise Data Dictionary Ver1.
// `status`: 'live' | 'partial' | 'planned' — controls badge + click behaviour.
// Order matches the Ver1 spec (Executive first, Admin last).
// ============================================================
const NAV = [
  // 1. Executive / Home — LIVE Ver1 hero module #4
  { to: "/",              label: "Executive / Home",              icon: LayoutDashboard, tid: TID.navDashboard,        module: "dashboard",   status: "live",    end: true, hideFor: ["admin", "finance_admin"] },
  // 2. Financial — LIVE Ver1 hero module #3 (cutover management at /finance/legacy)
  { to: "/finance",       label: "Financial",                     icon: BarChart3,       tid: TID.navFinance,          module: "finance",     status: "live" },
  // 2b. BU Performance — sits directly under Financial (cross-cutting exec / BU manager view)
  { to: "/performance",   label: "BU Performance",                icon: Target,          tid: TID.navPerformance,      module: "performance", status: "live" },
  // 3. CRM / Clients — Ver1 rebuild in progress
  { to: "/crm",           label: "CRM · Clients",                 icon: UsersRound,      tid: TID.navCRM,              module: "crm",         status: "live" },
  { to: "/feedback",      label: "Feedback & CSAT",               icon: MessageCircle,   tid: "nav-feedback-link",     module: "crm",         status: "live" },
  // 4. Operations — LIVE (Ver1 hero module #2)
  { to: "/operations",    label: "Operations",                    icon: Wrench,          tid: "nav-operations-link",   module: "crm",         status: "live" },
  // 5. Asset Intelligence & Capital Planning — Capital already live
  { to: "/assets",        label: "Asset Intelligence",            icon: Warehouse,       tid: TID.navAssets,           module: "assets",      status: "partial" },
  // 6. HSEQ & Sustainability — HSEQ page live
  { to: "/hseq",          label: "HSEQ & Sustainability",         icon: HardHat,         tid: TID.navHSEQ,             module: "hseq",        status: "partial" },
  // 7. HR & Workforce — HR Hub scaffolded
  { to: "/hr",            label: "HR & Workforce",                icon: Users,           tid: TID.navHR,               module: "hr",          status: "partial" },
  // 8. Fleet & Vehicles — Vehicles page live
  { to: "/vehicles",      label: "Fleet & Vehicles",              icon: Truck,           tid: "nav-vehicles-link",     module: "vehicles",    status: "partial" },
  // 9. Sales & Estimating — LIVE
  { to: "/sales",         label: "Sales & Estimating",            icon: TrendingUp,      tid: "nav-sales-link",        module: "sales",       status: "live" },
  // 10. Contracts — LIVE
  { to: "/contracts",     label: "Contracts",                     icon: FileSignature,   tid: "nav-contracts-link",    module: "contracts",   status: "live" },
  // 11. Reports — LIVE (cross-module builder)
  { to: "/reports",         label: "Reports",                       icon: FileBarChart,    tid: "nav-reports-link",       module: "reports",     status: "live" },
  { to: "/report-builder",  label: "Report Builder",                icon: Sparkles,        tid: "nav-report-builder-link", module: "reports",    status: "live" },
  // 12. Administration — collected under the Admin group below
  // (Cross-cutting) — BU Performance now lives directly under Financial above.
  // Client Portal (admin preview / client landing)
  { to: "/portal",        label: "Client Portal",                 icon: UsersRound,      tid: "nav-portal-link",       module: "client_portal", status: "live" },
  // Utility (kept last)
  { to: "/search",        label: "Search",                        icon: Search,          tid: TID.navSearch,           module: "dashboard",   status: "live" },
  { to: "/help",          label: "Help & How-to",                 icon: LifeBuoy,        tid: "nav-help-link",         module: "dashboard",   status: "live" },
];

const ROLE_LABEL = {
  executive: "Executive",
  ops_mgmt: "Operations Mgmt",
  bu_manager: "BU Manager",
  bu_support: "BU Support",
  admin: "Administration",
  finance_admin: "Financial Administration",
  field_worker: "Field Worker",
  client: "Client",
  sustainability_advisor: "Sustainability Advisor",
  hseq_auditor: "HSEQ Auditor",
};

export function Sidebar() {
  const { user, logout, isImpersonating } = useAuth();
  const { role, canSee } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { open, setOpen } = useSidebar();

  // Close drawer whenever the route changes (mobile UX)
  useEffect(() => { setOpen(false); }, [location.pathname, setOpen]);

  const initials = (user?.name || user?.email || "?")
    .split(/[\s@.]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  const ROLE_NAV = { client: ["/portal", "/assets", "/hseq"], sustainability_advisor: ["/hseq"],
    hseq_auditor: ["/feedback", "/hseq", "/hr"] };
  const roleNav = ROLE_NAV[role];
  const visibleNav = NAV.filter((n) => (!n.module || canSee(n.module)
      || (n.module === "hseq" && canSee("sustainability"))) && !(n.hideFor || []).includes(role))
    .filter((n) => !roleNav || roleNav.includes(n.to));
  // General Administration ("admin") + Financial Administration are processing roles — no admin surfaces.
  const canOrg = canSee("org_mgmt") && !["bu_manager", "bu_support", "admin", "finance_admin"].includes(role);
  // Financial Administration keeps the Import Hub (data feeds) without the other admin surfaces.
  const canImports = canOrg || role === "finance_admin";
  // Integrations + Security are admin surfaces — hide for external Client and mobile Field Worker
  const canAdminSurfaces = !["client", "field_worker", "bu_manager", "bu_support", "admin", "finance_admin", "sustainability_advisor", "hseq_auditor"].includes(role);

  // Pending write-back count for the badge dot
  const [pendingWrites, setPendingWrites] = useState(0);
  useEffect(() => {
    if (!canSee("org_mgmt") || !canAdminSurfaces) return;
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const { data } = await api.get("/governance/queue?status=pending");
        if (!cancelled) setPendingWrites(data.length);
      } catch (e) {
        if (e?.response?.status !== 401) console.warn("[Sidebar] pending count failed", e?.message);
      }
    };
    fetchPending();
    const t = setInterval(fetchPending, 60000);  // refresh every 60s
    return () => { cancelled = true; clearInterval(t); };
  }, [canSee]);

  return (
    <>
      {/* Mobile overlay backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm lg:hidden"
          onClick={() => setOpen(false)}
          aria-label="Close menu"
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        data-testid="sidebar"
        className={`fixed left-0 ${isImpersonating ? "top-11" : "top-0"} bottom-0 w-64 bg-gbe text-slate-100 flex flex-col z-40 border-r border-white/10
                    transform transition-transform duration-200 ease-out
                    ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        <div className="px-5 py-5 border-b border-white/10/70 flex items-center gap-2">
          <div className="flex-1 min-w-0"><GBELogo /></div>
          <button
            className="lg:hidden p-1.5 rounded hover:bg-white/10 text-white/80"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            data-testid="sidebar-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        <div className="px-2 pb-2 text-[10px] uppercase tracking-[0.16em] text-white/50">Workspace</div>
        {visibleNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.tid}
            className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}
          >
            <n.icon className="side-icon" />
            <span className="flex-1 truncate">{n.label}</span>
            {n.status === "planned" && (
              <span data-testid={`${n.tid}-badge-soon`} className="ml-auto text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-400/30">
                Soon
              </span>
            )}
            {n.to === "/admin/integrations" && pendingWrites > 0 && (
              <span data-testid="sidebar-integrations-pending-badge" className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-semibold">
                {pendingWrites}
              </span>
            )}
          </NavLink>
        ))}

        <div className="px-2 pt-5 pb-2 text-[10px] uppercase tracking-[0.16em] text-white/50">Administration</div>
        {canOrg && (
          <NavLink to="/org" data-testid="nav-org-link" className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
            <Building2 className="side-icon" />
            <span>Organisation</span>
          </NavLink>
        )}
        {canOrg && (
          <NavLink to="/admin/data-governance" data-testid="nav-data-governance-link" className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
            <ScrollText className="side-icon" />
            <span className="flex-1">Data Governance</span>
          </NavLink>
        )}
        {canOrg && (
          <NavLink to="/admin/data-provenance" data-testid="nav-data-provenance-link" className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
            <BadgeCheck className="side-icon" />
            <span className="flex-1">Data Provenance</span>
          </NavLink>
        )}
        {canOrg && (
          <NavLink to="/admin/integrations" data-testid="nav-integrations-link" className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
            <Cable className="side-icon" />
            <span className="flex-1">Integrations</span>
            {pendingWrites > 0 && (
              <span data-testid="sidebar-integrations-pending-badge-admin" className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-semibold">
                {pendingWrites}
              </span>
            )}
          </NavLink>
        )}
        {canOrg && (
          <NavLink to="/admin/permission-groups" data-testid="nav-permission-groups-link" className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
            <ShieldCheck className="side-icon" />
            <span className="flex-1">Permission Groups</span>
          </NavLink>
        )}
        {canImports && (
          <NavLink to="/admin/imports" data-testid="nav-imports-link" className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
            <Upload className="side-icon" />
            <span className="flex-1">Import Hub</span>
          </NavLink>
        )}
        {canOrg && (
          <NavLink to="/audit" data-testid={TID.navAudit} className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
            <FileSpreadsheet className="side-icon" />
            <span>Audit Log</span>
          </NavLink>
        )}
        {canAdminSurfaces && (
          <NavLink to="/settings" data-testid={TID.navSettings} className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
            <Settings className="side-icon" />
            <span>Client Integrations</span>
          </NavLink>
        )}
        <NavLink to="/support" data-testid="nav-support-link" className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
          <LifeBuoy className="side-icon" />
          <span>Support</span>
        </NavLink>
        <NavLink to="/security" data-testid={TID.navSecurity} className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
          <ShieldCheck className="side-icon" />
          <span>Security</span>
        </NavLink>
      </nav>

      <div className="px-3 py-3 border-t border-white/10/70">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={TID.topbarUser}
              className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-white/5 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-xs font-medium text-white">
                {initials || "GB"}
              </div>
              <div className="text-left flex-1 min-w-0">
                <div className="text-sm text-white truncate">{user?.name || "User"}</div>
                <div className="text-[11px] text-white/60 truncate">
                  {role ? ROLE_LABEL[role] || role : user?.email}
                </div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-56">
            <DropdownMenuLabel>Signed in</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={TID.topbarProfile} onClick={() => navigate("/security")}>
              <ShieldCheck className="w-4 h-4 mr-2" />
              Security & 2FA
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <Settings className="w-4 h-4 mr-2" />
              Integrations
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={TID.topbarLogout} onClick={logout} className="text-rose-600 focus:text-rose-700">
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
    </>
  );
}
